import './MiniCart.scss';

import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';

import Price from '@/common/components/blocks/Price/Price';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import {
  ROLES,
  ROUTE_CARTS,
  ROUTE_ECATALOGS,
  ROUTE_PRODUCT,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import { useMiniCartContext } from '@/common/contexts/MiniCartContext/MiniCartContext';
import { ReactComponent as BasketSvg } from '@/ebat/assets/icons/basket.svg';
import useCart from '@/ebat/hooks/carts/useCart';
import useManageCarts from '@/ebat/hooks/carts/useManageCarts';

import Loader from '../../Common/Loader/Loader';
import { MiniCartDefaultProps, MiniCartPropTypes } from './MiniCart.proptypes';
import MiniCartProduct from './MinicartProduct/MiniCartProduct';

const MiniCart = ({ className }) => {
  const {
    isVisible,
    setIsVisible,
    cartButtonRef,
    resetTimeout,
    setIsMouseInMiniCart,
    timeoutId,
  } = useMiniCartContext();
  const { defaultCart } = useDefaultCartContext();
  const { user } = useAuth();
  const location = useLocation();
  const isCartPage = location.pathname.includes(ROUTE_CARTS);
  const isEcatalogPage = location.pathname.includes(`${ROUTE_ECATALOGS}/`);

  const { data: cart, refetch, isLoading: isLoadingCart, isFetching } = useCart(
    {
      id: defaultCart?.id,
      setDefault: true,
      disabled:
        !user.role || user.role === ROLES.CUSTOMER_PRESENTER || isCartPage,
    },
  );

  const { t } = useTranslation();
  const miniCartRef = useRef(null);
  const {
    localCart,
    onPutCartItems,
    setLocalCart,
    onDeleteCartItem,
  } = useManageCarts({
    cart,
    refetchCart: refetch,
  });
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const isLoadingMiniCart = isLoadingCart || isFetching || isLoading;

  const handleUpdateQuantity = () => {
    setIsLoading(true);
    onPutCartItems({
      onSuccess: () => {
        refetch();
        setIsLoading(false);
      },
      items: localCart.items,
    });
  };

  const handleQuantityChange = (productId, newQuantity) => {
    setLocalCart({
      ...localCart,
      items: localCart?.items.map((item) => {
        if (item.id === productId) {
          return { ...item, quantity: newQuantity };
        }
        return { ...item, quantity: item.quantity };
      }),
    });
  };

  const handleDeleteCartItem = ({ id }) => {
    setIsLoadingDelete(true);
    onDeleteCartItem({
      id,
      onSuccess: () => {
        setLocalCart({
          ...localCart,
          items: localCart.items.filter((item) => item.id !== id),
        });
        setIsLoadingDelete(false);
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifiez si le clic est en dehors du MiniCart
      if (
        miniCartRef.current &&
        !miniCartRef.current.contains(event.target) &&
        !cartButtonRef?.current?.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    // Ajoutez un gestionnaire d'événement au document
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Nettoyez le gestionnaire d'événement lors du démontage
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [miniCartRef, cartButtonRef, setIsVisible]);

  useEffect(() => {
    // Fonction qui vérifie si la souris est dans le MiniCart ou sur le bouton du panier
    const checkMousePosition = (event) => {
      const miniCartRect = miniCartRef.current?.getBoundingClientRect();
      const cartButtonRect = cartButtonRef?.current?.getBoundingClientRect();

      const isMouseInMiniCart =
        miniCartRect &&
        event.clientX >= miniCartRect.left &&
        event.clientX <= miniCartRect.right &&
        event.clientY >= miniCartRect.top &&
        event.clientY <= miniCartRect.bottom;

      const isMouseInCartButton =
        cartButtonRect &&
        event.clientX >= cartButtonRect.left &&
        event.clientX <= cartButtonRect.right &&
        event.clientY >= cartButtonRect.top &&
        event.clientY <= cartButtonRect.bottom;

      if (isMouseInMiniCart || isMouseInCartButton) {
        setIsMouseInMiniCart(true);
      } else {
        setIsMouseInMiniCart(false);
      }
    };

    if (!isLoadingMiniCart) {
      resetTimeout();
    } else {
      clearTimeout(timeoutId.current);
    }

    document.addEventListener('mousemove', checkMousePosition);

    return () => {
      document.removeEventListener('mousemove', checkMousePosition);
    };
  }, [
    cartButtonRef,
    isLoading,
    isLoadingMiniCart,
    resetTimeout,
    setIsMouseInMiniCart,
    timeoutId,
  ]);

  const hasModifiedItems = cart?.items.some((originalItem, index) => {
    const localItem = localCart?.items?.[index];
    return (
      originalItem?.id !== localItem?.id ||
      originalItem?.quantity !== localItem?.quantity
    );
  });

  useEffect(() => {
    if (
      !isVisible ||
      defaultCart?.nb_items === 0 ||
      isCartPage ||
      isEcatalogPage
    ) {
      setIsVisible(false);
    }
  }, [isVisible, isCartPage, isEcatalogPage, setIsVisible, defaultCart]);

  if (
    !isVisible ||
    defaultCart?.nb_items === 0 ||
    isCartPage ||
    isEcatalogPage
  ) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={cn(className, 'mini-cart')} ref={miniCartRef}>
      <div className="mini-cart__header">
        <span
          className={cn('header__total', {
            'header__total--loading': isLoadingMiniCart,
          })}
        >
          {!isLoadingCart && !isFetching && !isLoading && (
            <>
              {t('ebat:entities:mini-cart.total')}
              <Price
                value={!hasModifiedItems ? cart?.subtotal : null}
                sup={false}
              />
            </>
          )}
        </span>
      </div>
      {isLoadingMiniCart ? (
        <div className="mini-cart__loader">
          <Loader type="full" />
          <span className="mini-cart__loading-cart">
            {t('common:messages:actions.mini-cart.update')}
          </span>
        </div>
      ) : (
        <div className="mini-cart__scroll-container">
          <div className="mini-cart__product-container">
            {localCart &&
              localCart.items.map(
                (product, i) =>
                  ((product.diff && product.is_selected) ||
                    product.diff === null) && (
                    <div key={i}>
                      {product?.product ? (
                        <NavLink
                          to={`${ROUTE_PRODUCT}/${product?.product?.path}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <MiniCartProduct
                            product={product}
                            cart={cart}
                            isLoadingDelete={isLoadingDelete}
                            onQuantityChange={handleQuantityChange}
                            onDeleteCartItem={handleDeleteCartItem}
                          />
                          {i < localCart.items.length - 1 && (
                            <div className="mini-cart__hr" />
                          )}
                        </NavLink>
                      ) : (
                        <>
                          <MiniCartProduct
                            product={product}
                            cart={cart}
                            isLoadingDelete={isLoadingDelete}
                            onQuantityChange={handleQuantityChange}
                            onDeleteCartItem={handleDeleteCartItem}
                          />
                          {i < localCart.items.length - 1 && (
                            <div className="mini-cart__hr" />
                          )}
                        </>
                      )}
                    </div>
                  ),
              )}
          </div>
        </div>
      )}
      <div className="mini-cart__footer">
        <ButtonLoading
          className="mini-cart__button"
          buttonSize="x-large"
          variant={hasModifiedItems ? 'primary-light' : 'primary'}
          onClick={() => {
            if (hasModifiedItems) {
              handleUpdateQuantity();
            } else {
              history.push(`${ROUTE_CARTS}/${cart.id}`);
              setIsVisible(false);
            }
          }}
          isLoading={isLoadingMiniCart}
        >
          {hasModifiedItems ? (
            t('ebat:entities:mini-cart.validate')
          ) : (
            <>
              <BasketSvg className="button__icon" />
              {t('ebat:entities:common.modal.ia.actions.see_cart')}
            </>
          )}
        </ButtonLoading>
      </div>
    </div>,
    document.body,
  );
};

MiniCart.defaultProps = {
  ...MiniCartDefaultProps,
};

MiniCart.propTypes = {
  ...MiniCartPropTypes,
};

export default MiniCart;
